/**
 * CMS Article Queries
 *
 * Server-side queries for fetching articles with pagination and by slug.
 */

import { query } from "@solidjs/router";
import type { CMSArticle, CMSArticleResponse } from "~/types/cms";
import { env } from "~/config/env";
import { isPreviewTokenValid } from "~/utils/previewToken.server";
import { logger } from "~/lib/logger/logger";
import { fetchFromCMS, findByTenant, emptyPaginatedResponse } from "../queryUtils";
import { buildArticlesListUrl, buildArticleBySlugUrl } from "../queryUrls";

/**
 * Server-side query for fetching all articles with pagination
 *
 * @param tenantSlug - The tenant slug for multi-tenancy filtering
 * @param page - Page number (default: 1)
 * @param limit - Number of articles per page (default: 10)
 * @param station - Optional station filter
 * @returns Paginated articles list
 */
export const getAllArticlesQuery = query(
  async (tenantSlug: string, page: number = 1, limit: number = 10, station?: string) => {
    "use server";

    const LOG_PREFIX = "[allArticles]";

    // URL built via shared queryUrls module (also used by performance audit)
    const url = `${env.cmsApiUrl}${buildArticlesListUrl({ page, limit, station, tenantSlug })}`;

    const data = await fetchFromCMS<CMSArticleResponse>(url, { logPrefix: LOG_PREFIX });

    if (!data?.docs || !Array.isArray(data.docs)) {
      logger.warn(`${LOG_PREFIX} Invalid response structure`);
      return emptyPaginatedResponse<CMSArticle>(page, limit);
    }

    logger.info(`${LOG_PREFIX} Found ${data.docs.length} articles (total: ${data.totalDocs || 0})`);
    return data;
  },
  "allArticles"
);

/**
 * Server-side query for fetching an article by slug
 *
 * @param slug - The article slug (e.g., 'my-article')
 * @param tenantSlug - The tenant slug for multi-tenancy filtering
 * @param previewToken - CMS-signed preview token; drafts are read only when it verifies server-side for the tenant
 * @returns Article data or null if not found
 */
export const getArticleBySlugQuery = query(
  async (slug: string, tenantSlug: string, previewToken?: string) => {
    "use server";
    const draft = isPreviewTokenValid(previewToken, tenantSlug);

    const LOG_PREFIX = "[articleBySlug]";

    // URL built via shared queryUrls module (also used by performance audit)
    const url = `${env.cmsApiUrl}${buildArticleBySlugUrl({ slug, tenantSlug, draft })}`;

    const data = await fetchFromCMS<CMSArticleResponse>(url, {
      logPrefix: LOG_PREFIX,
      previewToken: draft ? previewToken : undefined,
    });

    if (!data?.docs || data.docs.length === 0) {
      logger.warn(`${LOG_PREFIX} No article found with slug: ${slug}`);
      return null;
    }

    // Payload ignores nested tenant[slug] filters — filter client-side
    const article = findByTenant(data.docs, tenantSlug);

    if (!article) {
      logger.warn(`${LOG_PREFIX} No article found for tenant: ${tenantSlug}`);
      return null;
    }

    logger.info(`${LOG_PREFIX} Found article: ${article.title}`);
    return article as CMSArticle;
  },
  "articleBySlug"
);
